// src/App.js
import "./App.css";
import React, { useState } from "react";
import { Routes, Route } from "react-router-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { uploadData } from "aws-amplify/storage";

import { AuthProvider, useAuth } from "./AuthProvider"; // ← wrap & use context

import SignInWithPCCButton from "./components/SignInWithPCCButton";
import AudioRecorder from "./components/AudioRecorder";
import SessionBrowser from "./components/SessionBrowser";
import PCCCallbackHandler from "./routes/PCCCallbackHandler";
import PrintSessionResultView from "./components/PrintSessionResultView";
import CodingReviewScreen from "./components/CodingReviewScreen";
import LOMNScreen from "./components/LOMNScreen";
import DeveloperDashboard from "./components/DeveloperDashboard";
import BriefMe from "./components/BriefMe";
import { usePCCSession } from "./components/hooks/usePCCSession";
import LoginView from "./components/LoginView"; // keep your existing path/casing
import { SUPPORT_MAILTO } from "./components/support"; // ← support link
import VerifyEmailHandler from "./routes/VerifyEmailHandler";

// -----------------------------
// Dashboard (uses Auth context)
// -----------------------------
function AppMenuBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { token } = usePCCSession();
  const hasPCCSession = !!token;
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navItems = [
    { path: "/", label: "Dashboard" },
    { path: "/coding-review", label: "Medical Coding Review" },
    { path: "/lomn", label: "Letter of Medical Necessity" },
  ];

  if (user?.isDeveloper) {
    navItems.push({ path: "/developer-dashboard", label: "Developer Dashboard" });
  }

  return (
    <div className={`app-menu-bar${isMenuOpen ? " is-open" : ""}`}>
      <button
        type="button"
        className="app-menu-toggle"
        aria-expanded={isMenuOpen}
        aria-controls="app-menu-content"
        onClick={() => setIsMenuOpen((open) => !open)}
      >
        <span aria-hidden="true">☰</span>
        <span>{isMenuOpen ? "Close menu" : "Menu"}</span>
      </button>

      <div id="app-menu-content" className="app-menu-content">
      <div className="app-menu-primary">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.path}
              onClick={() => {
                setIsMenuOpen(false);
                navigate(item.path);
              }}
              className={`button app-menu-button${isActive ? " is-active" : ""}`}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      <div className="app-menu-secondary">
        <div className="app-menu-user">{user?.username || "unknown user"}</div>
        {!hasPCCSession ? <SignInWithPCCButton /> : <div className="app-menu-pill">PCC Connected</div>}
        <button onClick={logout} className="button danger app-menu-signout">
          Sign Out
        </button>
      </div>
      </div>
    </div>
  );
}

const NOTE_TEMPLATE_OPTIONS = [
  { value: "STANDARD_MEDICAL", label: "Standard Medical Note" },
  { value: "BEHAVIOR_THERAPY", label: "Behavior Therapy Note (Psychology/Psychiatry)" },
];

function Dashboard() {
  const { user, setAutoLogoutSuppressed } = useAuth();
  const { token, orgUuid, pccUserId, facilitiesOptions, quotaExceeded } = usePCCSession();
  const hasPCCSession = !!token;
  const [showBriefMe, setShowBriefMe] = useState(false);
  const [noteTemplate, setNoteTemplate] = useState(NOTE_TEMPLATE_OPTIONS[0].value);

  const handleAudioStart = () => {
    // Pause inactivity auto-logout while recording/session is active
    setAutoLogoutSuppressed(true);
  };

  const handleAudioStop = (blob, metadata) => {
    // Resume inactivity auto-logout now that active recording ended
    setAutoLogoutSuppressed(false);

    console.log("🎙️ Recording stopped.");
    console.log("🕰️ Metadata:", metadata);

    const clonedMetadata = { ...metadata, noteTemplate };
    const sessionId = clonedMetadata.sessionId; // ✅ Use the passed sessionId

    uploadToS3(user, blob, clonedMetadata, sessionId);
  };

  const uploadToS3 = async (currentUser, blob, metadata, sessionId) => {
    const folder = `${currentUser.username}/recordings/`;
    const audioKey = `${folder}${sessionId}.webm`;
    const metadataKey = `${folder}${sessionId}-meta.json`;

    try {
      // Upload audio
      await uploadData({
        key: audioKey,
        data: blob,
        options: {
          accessLevel: "private",
          contentType: blob.type,
        },
      }).result;

      console.log("✅ Upload success to S3:", audioKey);

      // Upload metadata as JSON
      const metadataJSON = JSON.stringify(metadata);
      await uploadData({
        key: metadataKey,
        data: metadataJSON,
        options: {
          accessLevel: "private",
          contentType: "application/json",
        },
      }).result;

      console.log("📎 Metadata upload success:", metadataKey);
      console.log("📅 Time In:", metadata.timeIn);
      console.log("📅 Time Out:", metadata.timeOut);
      console.log("⏱️ Duration (minutes):", metadata.durationMinutes);

      alert("🎉 Audio and metadata uploaded! AI analysis will be processed shortly.");
    } catch (error) {
      console.error("❌ Upload failed:", error);
      alert("Upload failed ❌");
    }
  };

  return (
    <div className="container dashboard-container">
      <h2>Session Browser: {user.username}</h2>

      <div className="top-controls">
        <label className="note-template-select">
          Note Template:&nbsp;
          <select
            value={noteTemplate}
            onChange={(e) => setNoteTemplate(e.target.value)}
          >
            {NOTE_TEMPLATE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        {/* 
          IMPORTANT: 
          Your AudioRecorder should invoke these props when recording starts/stops.
          If it doesn't yet, add calls inside your component:
            props.onStartRecording?.();
            props.onStopRecording?.(blob, metadata);
        */}
        <AudioRecorder onStartRecording={handleAudioStart} onStopRecording={handleAudioStop} />
        {hasPCCSession && (
          <button onClick={() => setShowBriefMe(true)} className="button save">
            🔎 Brief Me
          </button>
        )}
      </div>

      {showBriefMe && (
        <BriefMe
          orgUuid={orgUuid}
          pccUserId={pccUserId}
          token={token}
          facilitiesOptions={facilitiesOptions}
          quotaExceeded={quotaExceeded}
          onClose={() => setShowBriefMe(false)}
        />
      )}

      <div className="session-browser-shell">
        <SessionBrowser userId={user.username} />
      </div>
    </div>
  );
}

// Simple global footer with support link
function AppFooter() {
  return (
    <footer className="app-footer" style={{ marginTop: 24, padding: 16, textAlign: "center" }}>
      <a href={SUPPORT_MAILTO} rel="noopener noreferrer">
        support@creativepracticesolutions.com
      </a>
    </footer>
  );
}

// -----------------------------
// Routes that depend on auth
// -----------------------------
function AppRoutes() {
  const location = useLocation();
  const { user, login } = useAuth();

  if (location.pathname === "/verify-email") {
    return <VerifyEmailHandler />;
  }

  // If not signed in, show your existing login view.
  // When the user successfully signs in, call `login()` to sync the context.
  if (!user) {
    return (
      <>
        <LoginView onLogin={login} />
        <AppFooter />
      </>
    );
  }

  return (
    <>
      <AppMenuBar />
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/coding-review" element={<CodingReviewScreen />} />
        <Route path="/lomn" element={<LOMNScreen />} />
        <Route
          path="/developer-dashboard"
          element={
            user?.isDeveloper ? (
              <DeveloperDashboard />
            ) : (
              <div className="container">
                <h2>Not authorized</h2>
                <p>This dashboard is only available to users in the developer access list.</p>
              </div>
            )
          }
        />
        <Route path="/pcc/callback" element={<PCCCallbackHandler />} />
        <Route path="/session/:sessionId/print" element={<PrintSessionResultView />} />
      </Routes>
      <AppFooter />
    </>
  );
}

// -----------------------------
// Top-level App: wrap with AuthProvider
// -----------------------------
export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
